import axios from 'axios';
import { adminApiUrl } from '../config/api';
import { adminAuthService } from './adminAuth';

export interface CustomerAd {
  adId: string;
  userId: string;
  status: 'pending' | 'approved' | 'rejected';
  imageUrl: string;
  /** Presigned GET; required for private bucket. */
  imageViewUrl?: string;
  s3Key: string;
  caption?: string;
  createdAt: string;
  updatedAt: string;
  reviewedAt?: string;
  reviewedByAdminId?: string;
  rejectionReason?: string;
}

export interface CustomerAdListResponse {
  ads: CustomerAd[];
  count: number;
  lastEvaluatedKey?: string;
}

export interface ListCustomerAdsParams {
  limit?: number;
  lastEvaluatedKey?: string;
  status?: string;
}

class CustomerAdsAdminService {
  private api = axios.create({
    baseURL: adminApiUrl,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  constructor() {
    this.api.interceptors.request.use((config) => {
      const token = adminAuthService.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          adminAuthService.logout();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async listCustomerAds(params?: ListCustomerAdsParams): Promise<CustomerAdListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.lastEvaluatedKey) queryParams.append('lastEvaluatedKey', params.lastEvaluatedKey);
    if (params?.status) queryParams.append('status', params.status);

    const response = await this.api.get<{ data: CustomerAdListResponse }>(
      `/admin/customer-ads?${queryParams.toString()}`
    );
    return response.data.data;
  }

  async approveCustomerAd(adId: string): Promise<CustomerAd> {
    const response = await this.api.put<{ data: { ad: CustomerAd } }>(
      `/admin/customer-ads/${adId}/approve`
    );
    return response.data.data.ad;
  }

  async rejectCustomerAd(adId: string, rejectionReason?: string): Promise<CustomerAd> {
    const response = await this.api.put<{ data: { ad: CustomerAd } }>(
      `/admin/customer-ads/${adId}/reject`,
      rejectionReason ? { rejectionReason } : {}
    );
    return response.data.data.ad;
  }

  async deleteCustomerAd(adId: string): Promise<void> {
    await this.api.delete(`/admin/customer-ads/${adId}`);
  }
}

export const customerAdsAdminService = new CustomerAdsAdminService();
